import { isValid, parse } from 'date-fns';
import { z } from 'zod';
import { DAYS } from '~shared/constants/days';

const isValidDateFormat = (value: string) => {
  try {
    // Try MM-dd-yyyy format
    let parsedDate = parse(value, 'MM-dd-yyyy', new Date());
    if (isValid(parsedDate)) {
      return true;
    }

    // Try MM/dd/YYYY format
    parsedDate = parse(value, 'MM/dd/yyyy', new Date());
    return isValid(parsedDate);
  } catch (error) {
    console.error('🚀 ~ isValidDateFormat ~ error:', error);
    return false;
  }
};

// Base schema without refine
const baseClassSchema = z.object({
  Title: z.string().min(1, 'Title is required'),

  'Class Color': z
    .string()
    .regex(/^#([A-Fa-f0-9]{6})$/, 'Invalid hex color. Example: #FFFFFF')
    .optional(),

  'Hide Class Status': z.enum(['yes', 'no'], {
    errorMap: () => ({ message: 'Hide Class Status must be "yes" or "no"' }),
  }),

  Location: z.string().optional(),

  Room: z.string().optional(),

  'start age year': z.coerce
    .number()
    .min(0, 'Start age year must be at least 0')
    .max(99, 'Start age year must be at most 99'),

  'start age month': z.coerce
    .number()
    .min(0, 'Start age month must be at least 0')
    .max(11, 'Start age month must be at most 11'),

  'end age year': z.coerce
    .number()
    .min(0, 'End age year must be at least 0')
    .max(99, 'End age year must be at most 99'),

  'end age month': z.coerce
    .number()
    .min(0, 'End age month must be at least 0')
    .max(11, 'End age month must be at most 11'),

  Instructor: z.string().optional(),

  'Description (Under 200 Characters)': z
    .string()
    .max(200, 'Description must be under 200 characters')
    .optional(),

  Session: z.string(),

  'Start Date': z
    .string()
    .refine(
      isValidDateFormat,
      'Invalid start date format. Example: 12/31/2024',
    ),

  'End Date': z
    .string()
    .refine(isValidDateFormat, 'Invalid end date format. Example: 12/31/2024'),

  'Registration Start Date': z
    .string()
    .refine(
      isValidDateFormat,
      'Invalid registration start date format. Example: 12/31/2024',
    )
    .optional(),

  Days: z
    .string()
    .refine(
      (value) => {
        if (!value || value.trim() === '') {
          return false;
        }
        // Split by comma and trim each value
        const days = value.split(',').map((day) => day.trim());
        // Check if all days are valid
        const allValid = days.every((day) => (DAYS as ReadonlyArray<string>).includes(day));
        if (!allValid) {
          return false;
        }
        // Check for duplicates
        const uniqueDays = new Set(days);
        return uniqueDays.size === days.length;
      },
      {
        message: `Days must be one or more of: ${(DAYS as ReadonlyArray<string>).join(', ')}. Multiple days should be comma-separated. Duplicate values are not allowed.`,
      },
    ),

  'Start Time': z
    .string()
    .regex(
      /^(0?[1-9]|1[0-2])[:.][0-5][0-9] (AM|PM)$/,
      'Invalid time format. Example: 10:00 AM or 10.00 AM',
    ),

  'End Time': z
    .string()
    .regex(
      /^(0?[1-9]|1[0-2])[:.][0-5][0-9] (AM|PM)$/,
      'Invalid time format. Example: 11:30 AM or 11.30 AM',
    ),

  'Registration Fee': z.coerce
    .number()
    .nonnegative('Registration Fee must be a non-negative number')
    .optional(),

  'Tuition Fee': z.coerce
    .number()
    .nonnegative('Tuition Fee must be a non-negative number')
    .optional(),

  'Costume Fee': z.coerce.number().nonnegative().optional(),

  'Class Size': z.coerce
    .number()
    .positive('Class Size must be a positive number')
    .max(300, 'Class Size must be at most 300'),

  Group: z.string().optional(),

  'Tuition Billing Cycle': z.preprocess(
    (val) => {
      if (typeof val === 'string') {
        const lowerVal = val.toLowerCase();
        if (lowerVal === 'monthly') return 'Monthly';
        if (lowerVal === 'one time') return 'One Time';
        if (lowerVal === 'weekly') return 'Weekly';
        if (lowerVal === 'bi-weekly') return 'Bi-Weekly';
        if (lowerVal === 'hourly') return 'Hourly';
        if (lowerVal === 'every x days' || lowerVal === 'every-x-days')
          return 'every-x-days';
      }
      return val;
    },
    z.enum([
      'Monthly',
      'One Time',
      'Weekly',
      'Bi-Weekly',
      'Hourly',
      'every-x-days',
    ]),
  ),

  'Billing Frequency Days': z.coerce.number().optional(),

  'Hourly Plan Name': z.string().optional(),

  'Billing Day': z.coerce.number().optional(),
});

// Full schema with cross-field validation
export const classSchema = baseClassSchema
  .refine(
    (data) => {
      // Only validate Billing Day when Tuition Billing Cycle is Monthly
      const billingDay = data['Billing Day'];
      if (data['Tuition Billing Cycle'] === 'Monthly') {
        if (billingDay) {
          return billingDay >= 1 && billingDay <= 28;
        } else {
          return false;
        }
      } else if (data['Tuition Billing Cycle'] === 'Hourly') {
        // Billing Day is not used for hourly billing
        return true;
      } else {
        return billingDay === 0 || billingDay == null;
      }
    },
    {
      message:
        'Billing Day must be between 1-28 when Tuition Billing Cycle is Monthly',
      path: ['Billing Day'], // This will attach the error to the Billing Day field
    },
  )
  .refine(
    (data) => {
      // Require Hourly Plan Name when Tuition Billing Cycle is Hourly
      if (data['Tuition Billing Cycle'] === 'Hourly') {
        const planName = data['Hourly Plan Name'];
        return (
          planName !== undefined &&
          typeof planName === 'string' &&
          planName.trim() !== ''
        );
      }
      return true;
    },
    {
      message:
        'Hourly Plan Name is required when Tuition Billing Cycle is Hourly',
      path: ['Hourly Plan Name'],
    },
  )
  .refine(
    (data) => {
      // Require Tuition Fee for non-hourly billing cycles
      if (data['Tuition Billing Cycle'] !== 'Hourly') {
        return data['Tuition Fee'] !== undefined;
      }
      return true;
    },
    {
      message: 'Tuition Fee is required for non-hourly billing cycles.',
      path: ['Tuition Fee'],
    },
  )
  .refine(
    (data) => {
      if (data['Tuition Billing Cycle'] === 'every-x-days') {
        const freq = data['Billing Frequency Days'];
        return freq !== undefined && freq >= 1;
      }
      return true;
    },
    {
      message:
        'Billing Frequency Days is required and must be at least 1 when Tuition Billing Cycle is Every X Days',
      path: ['Billing Frequency Days'],
    },
  )
  .refine(
    (data) => {
      if (data['Tuition Billing Cycle'] !== 'every-x-days') {
        return !data['Billing Frequency Days'];
      }
      return true;
    },
    {
      message:
        'Billing Frequency Days should only be set when Tuition Billing Cycle is Every X Days',
      path: ['Billing Frequency Days'],
    },
  );

export type ClassData = z.infer<typeof classSchema>;

const CROSS_FIELD_KEYS: Array<keyof ClassData> = [
  'Billing Day',
  'Hourly Plan Name',
  'Tuition Fee',
  'Billing Frequency Days',
];

export function validateCell(
  key: keyof ClassData,
  value: unknown,
  context?: Partial<ClassData>,
  hourlyPlanNames?: Array<string>,
): { isValid: boolean; errorMessage?: string } {
  if (CROSS_FIELD_KEYS.includes(key) && context) {
    // Create a partial object with the current value and context
    const testData = { ...context, [key]: value };

    // Validate the entire object to trigger cross-field validation
    const result = classSchema.safeParse(testData);

    if (result.success) {
      // Additional validation for Hourly Plan Name: check if it exists in available plans
      if (
        key === 'Hourly Plan Name' &&
        context['Tuition Billing Cycle'] === 'Hourly'
      ) {
        if (hourlyPlanNames && value && typeof value === 'string') {
          const planName = value.trim();
          if (!hourlyPlanNames.includes(planName)) {
            return {
              isValid: false,
              errorMessage: `Hourly Plan Name '${planName}' not found. Plan names are case-sensitive.`,
            };
          }
        }
      }
      return { isValid: true };
    } else {
      // Find error specific to the field
      const fieldError = result.error.errors.find((err) =>
        err.path.includes(key),
      );

      if (fieldError) {
        return {
          isValid: false,
          errorMessage: fieldError.message,
        };
      }

      // If no specific field error, the field itself is valid
      return { isValid: true };
    }
  }

  // For other fields, use the base schema shape (without refine)
  const schema = baseClassSchema.shape[key];
  // Unmapped / leftover CSV headers are not Enrollio fields — skip, don't crash.
  if (!schema) {
    return { isValid: true };
  }
  const result = schema.safeParse(value);

  if (result.success) {
    return { isValid: true };
  } else {
    return {
      isValid: false,
      errorMessage: result.error.errors[0]?.message ?? 'Unknown error',
    };
  }
}

interface AutoIncrementNameParts {
  base: string;
  suffix: number;
  original: string;
}

const AUTO_INCREMENT_MIN_RUN_LENGTH = 3;

function extractAutoIncrementNameParts(
  name: string,
): AutoIncrementNameParts | null {
  const trimmed = name.trim();
  if (!trimmed) return null;

  const match = /^(.*?)(\d+)$/.exec(trimmed);
  if (!match) return null;

  const suffix = parseInt(match[2], 10);
  if (Number.isNaN(suffix)) return null;

  return {
    base: match[1].trim(),
    suffix,
    original: trimmed,
  };
}

export function getDragFillErrors(
  rows: Array<ClassData>,
  column: 'Session' | 'Group' | 'Location',
): Map<number, string> {
  const errors = new Map<number, string>();
  const byBase = new Map<string, Array<{ index: number; suffix: number; value: string }>>();

  rows.forEach((row, index) => {
    const value = (row[column] ?? '').trim();
    if (!value) return;

    const parts = extractAutoIncrementNameParts(value);
    if (!parts) return;

    const entries = byBase.get(parts.base) ?? [];
    entries.push({ index, suffix: parts.suffix, value });
    byBase.set(parts.base, entries);
  });

  for (const [, entries] of byBase) {
    if (entries.length < AUTO_INCREMENT_MIN_RUN_LENGTH) continue;
    entries.sort((a, b) => a.suffix - b.suffix);

    let runStart = 0;
    for (let i = 1; i <= entries.length; i++) {
      const isEnd =
        i === entries.length ||
        entries[i].suffix !== entries[i - 1].suffix + 1;

      if (!isEnd) continue;

      const run = entries.slice(runStart, i);
      if (run.length >= AUTO_INCREMENT_MIN_RUN_LENGTH) {
        const canonical = run[0].value;
        const message = `${column} values appear to be auto-incremented. Use the same value (e.g., "${canonical}") for each row instead of dragging the fill handle.`;
        for (const item of run) {
          errors.set(item.index, message);
        }
      }
      runStart = i;
    }
  }

  return errors;
}
